/**
 * MyApplications Page
 * View and manage job applications
 * MOBILE RESPONSIVE VERSION
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  Clock, 
  CheckCircle, 
  XCircle,
  Eye,
  Calendar,
  Building,
  MapPin,
  DollarSign,
  Filter,
  Search,
  RefreshCw,
  Loader,
  AlertCircle,
  ChevronDown,
  X
} from 'lucide-react';
import { getUserApplications } from '../services/careerService';
import { useAuth } from '../../../core/contexts/AuthContext';

/**
 * MyApplications Page Component
 * 
 * @returns {JSX.Element} MyApplications component
 */
const MyApplications = () => {
  const { currentUser } = useAuth();
  
  const [applications, setApplications] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Load applications
  const loadApplications = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const userApplications = await getUserApplications(currentUser.uid);
      setApplications(userApplications);
      setFilteredApplications(userApplications);
    } catch (err) {
      console.error('Error loading applications:', err);
      setError(err.message || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    loadApplications();
  }, [currentUser]);

  // Filter applications
  useEffect(() => {
    let filtered = applications;

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(app => 
        app.jobDetails?.title.toLowerCase().includes(term) ||
        app.jobDetails?.company.name.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(app => app.status === statusFilter);
    }

    setFilteredApplications(filtered);
  }, [applications, searchTerm, statusFilter]);

  // Handle refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadApplications();
    setRefreshing(false);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, text: 'Pending' },
      reviewing: { color: 'bg-blue-100 text-blue-800', icon: Eye, text: 'Under Review' },
      accepted: { color: 'bg-green-100 text-green-800', icon: CheckCircle, text: 'Accepted' },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, text: 'Rejected' },
      interview: { color: 'bg-purple-100 text-purple-800', icon: Calendar, text: 'Interview' }
    };
    
    const badge = badges[status] || badges.pending;
    const IconComponent = badge.icon;
    
    return (
      <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        <span className="hidden sm:inline">{badge.text}</span>
        <span className="sm:hidden">{badge.text.split(' ')[0]}</span>
      </span>
    );
  };

  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get stats
  const getStats = () => {
    const total = applications.length;
    const pending = applications.filter(app => app.status === 'pending').length;
    const reviewing = applications.filter(app => app.status === 'reviewing').length;
    const accepted = applications.filter(app => app.status === 'accepted').length;
    const rejected = applications.filter(app => app.status === 'rejected').length;
    
    return { total, pending, reviewing, accepted, rejected };
  };

  const stats = getStats();
  const hasActiveFilters = searchTerm.trim() || statusFilter !== 'all';

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading your applications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0 flex-1">
              <Link
                to="/career-opportunities"
                className="flex items-center text-gray-600 hover:text-gray-900 mr-2 sm:mr-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">Back to Jobs</span>
                <span className="sm:hidden">Back</span>
              </Link>
              <div className="flex items-center min-w-0">
                <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 mr-2 flex-shrink-0" />
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
                  My Applications
                </h1>
              </div>
            </div>

            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors disabled:opacity-50 text-sm"
            >
              <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-6 sm:py-8 max-w-7xl mx-auto">
        {/* Stats Overview */}
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border text-center">
            <p className="text-lg sm:text-2xl font-bold text-gray-900">{stats.total}</p>
            <p className="text-xs sm:text-sm text-gray-600">Total</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border text-center">
            <p className="text-lg sm:text-2xl font-bold text-yellow-600">{stats.pending}</p>
            <p className="text-xs sm:text-sm text-gray-600">Pending</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border text-center">
            <p className="text-lg sm:text-2xl font-bold text-blue-600">{stats.reviewing}</p>
            <p className="text-xs sm:text-sm text-gray-600">Review</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border text-center">
            <p className="text-lg sm:text-2xl font-bold text-green-600">{stats.accepted}</p>
            <p className="text-xs sm:text-sm text-gray-600">Accepted</p>
          </div>
          <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm border text-center">
            <p className="text-lg sm:text-2xl font-bold text-red-600">{stats.rejected}</p>
            <p className="text-xs sm:text-sm text-gray-600">Rejected</p>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-6">
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 sm:w-5 sm:h-5" />
              <input
                type="text"
                placeholder="Search by job title or company..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>

          {/* Filter Controls */}
          <div className="flex items-center justify-between">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 transition-colors text-sm"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
              {hasActiveFilters && (
                <span className="ml-2 bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                  Active
                </span>
              )}
              <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${
                showFilters ? 'rotate-180' : ''
              }`} />
            </button>

            {hasActiveFilters && (
              <button
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('all');
                }}
                className="text-sm text-green-600 hover:text-green-700 flex items-center"
              >
                <X className="w-3 h-3 mr-1" />
                Clear
              </button>
            )}
          </div>

          {/* Status Filter */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full sm:w-48 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="reviewing">Under Review</option>
                <option value="interview">Interview</option>
                <option value="accepted">Accepted</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          </div>
        )}

        {/* Applications List */}
        {filteredApplications.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <FileText className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {applications.length === 0 ? 'No applications yet' : 'No applications match your filters'}
            </h3>
            <p className="text-gray-600 mb-6 px-4">
              {applications.length === 0 
                ? 'Start applying to jobs to see your applications here'
                : 'Try adjusting your search or filter criteria'
              }
            </p>
            {applications.length === 0 && (
              <Link
                to="/career-opportunities"
                className="inline-flex items-center px-4 sm:px-6 py-2 sm:py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm sm:text-base"
              >
                Browse Jobs
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredApplications.map((application) => (
              <div key={application.id} className="bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow">
                {/* Mobile Layout */}
                <div className="block sm:hidden p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                        {application.jobDetails?.company.logo ? (
                          <img 
                            src={application.jobDetails.company.logo} 
                            alt={application.jobDetails.company.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold text-sm">
                            {application.jobDetails?.company.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      
                      <div className="min-w-0 flex-1">
                        <h3 className="text-base font-semibold text-gray-900 mb-1 truncate">
                          {application.jobDetails?.title}
                        </h3>
                        <div className="space-y-1">
                          <div className="flex items-center text-xs text-gray-600">
                            <Building className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{application.jobDetails?.company.name}</span>
                          </div>
                          <div className="flex items-center text-xs text-gray-600">
                            <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                            <span>Applied {formatDate(application.appliedAt)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {getStatusBadge(application.status)}
                  </div>

                  {/* Mobile Details */}
                  {application.expectedSalary && (
                    <div className="mb-3 text-xs text-gray-600">
                      <span className="font-medium">Expected: </span>
                      ZAR {application.expectedSalary.toLocaleString()}
                    </div>
                  )}

                  {/* Cover Letter Preview - Mobile */}
                  {application.coverLetter && (
                    <div className="mb-3">
                      <p className="text-xs text-gray-700 line-clamp-2">
                        {application.coverLetter}
                      </p>
                    </div>
                  )}

                  {/* Status Message - Mobile */}
                  {application.statusMessage && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-3">
                      <span className="text-xs text-gray-600 font-medium">Update:</span>
                      <p className="text-xs text-gray-700 mt-1">{application.statusMessage}</p>
                    </div>
                  )}

                  {/* Mobile Actions */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div className="text-xs text-gray-500">
                      Updated: {formatDate(application.updatedAt || application.appliedAt)}
                    </div>
                    
                    <Link
                      to={`/career-opportunities/${application.jobId}`}
                      className="text-green-600 hover:text-green-700 text-xs font-medium flex items-center"
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View Job
                    </Link>
                  </div>
                </div>

                {/* Desktop Layout */}
                <div className="hidden sm:block p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-4 min-w-0 flex-1">
                      <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                        {application.jobDetails?.company.logo ? (
                          <img 
                            src={application.jobDetails.company.logo} 
                            alt={application.jobDetails.company.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center text-white font-bold">
                            {application.jobDetails?.company.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      
                      <div className="min-w-0 flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
                          {application.jobDetails?.title}
                        </h3>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 mr-1" />
                            <span>{application.jobDetails?.company.name}</span>
                          </div>
                          <div className="flex items-center">
                            <MapPin className="w-4 h-4 mr-1" />
                            <span>{application.jobDetails?.location}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {getStatusBadge(application.status)}
                  </div>

                  {/* Application Details - Desktop */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 text-sm">
                    <div>
                      <span className="text-gray-600">Applied on:</span>
                      <p className="font-medium">{formatDate(application.appliedAt)}</p>
                    </div>
                    
                    {application.expectedSalary && (
                      <div>
                        <span className="text-gray-600">Expected Salary:</span>
                        <p className="font-medium">ZAR {application.expectedSalary.toLocaleString()}</p>
                      </div>
                    )}
                    
                    <div>
                      <span className="text-gray-600">Availability:</span>
                      <p className="font-medium">{formatDate(application.availabilityDate)}</p>
                    </div>
                  </div>

                  {/* Cover Letter Preview - Desktop */}
                  {application.coverLetter && (
                    <div className="mb-4">
                      <span className="text-gray-600 text-sm">Cover Letter:</span>
                      <p className="text-gray-700 text-sm mt-1 line-clamp-2">
                        {application.coverLetter}
                      </p>
                    </div>
                  )}

                  {/* Status Message - Desktop */}
                  {application.statusMessage && (
                    <div className="bg-gray-50 p-3 rounded-lg mb-4">
                      <span className="text-gray-600 text-sm font-medium">Update from employer:</span>
                      <p className="text-gray-700 text-sm mt-1">{application.statusMessage}</p>
                    </div>
                  )}

                  {/* Actions - Desktop */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="text-sm text-gray-500">
                      Last updated: {formatDate(application.updatedAt || application.appliedAt)}
                    </div>
                    
                    <div className="flex items-center space-x-3">
                      <Link
                        to={`/career-opportunities/${application.jobId}`}
                        className="text-green-600 hover:text-green-700 text-sm font-medium flex items-center"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        View Job
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tips Section */}
        <div className="mt-6 sm:mt-8 bg-blue-50 p-4 sm:p-6 rounded-lg">
          <h3 className="font-medium text-blue-900 mb-3">Application Tips:</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Follow up on applications after 1-2 weeks if you haven't heard back</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Keep your CV and profile updated with latest experiences</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Prepare for interviews by researching the company and role</span>
            </li>
            <li className="flex items-start">
              <CheckCircle className="w-4 h-4 text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
              <span>Complete your audio screening to strengthen future applications</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default MyApplications;